/**
 * Auto-Migration Script: Legacy to Lit Components
 * Automatically converts legacy .scroll-reveal elements to <pm-scroll-reveal> components
 */

interface LegacyToLitMapping {
  animation: string;
  delay?: number;
  stagger?: boolean;
}

class LegacyMigration {
  private migrated: Set<HTMLElement> = new Set();
  private debug: boolean = false;

  constructor(enableDebug: boolean = false) {
    this.debug = enableDebug;
    this.log('🔄 Legacy Migration initialized');
  }

  /**
   * Main migration function - converts all legacy elements
   */
  public migrateAll(): void {
    this.log('Starting full migration...');
    
    // Migrate scroll reveal elements
    this.migrateScrollRevealElements();
    
    // Migrate stats counters
    this.migrateStatsCounters();
    
    // Clean up legacy classes
    this.cleanupLegacyClasses();
    
    this.log(`✅ Migration complete: ${this.migrated.size} elements migrated`);
  }

  /**
   * Migrate elements with .scroll-reveal class to <pm-scroll-reveal>
   */
  private migrateScrollRevealElements(): void {
    const legacyElements = document.querySelectorAll('.scroll-reveal:not(pm-scroll-reveal):not(pm-scroll-reveal *)');
    
    this.log(`Found ${legacyElements.length} legacy scroll-reveal elements`);
    
    legacyElements.forEach((element, index) => {
      const htmlElement = element as HTMLElement;
      
      if (this.migrated.has(htmlElement)) return;
      
      // Skip Bootstrap structural elements
      if (this.shouldSkipElement(htmlElement)) {
        this.log(`⏭️  Skipping Bootstrap element: ${htmlElement.className}`);
        return;
      }
      
      const config = this.analyzeLegacyElement(htmlElement);
      const wrapper = this.createScrollRevealComponent(config);
      
      // Handle staggered elements
      if (this.isStaggeredElement(htmlElement)) {
        config.stagger = true;
        const siblings = this.getStaggerSiblings(htmlElement);
        if (siblings.length > 1) {
          wrapper.setAttribute('stagger', 'true');
        }
      }
      
      // Wrap the element
      this.wrapElement(htmlElement, wrapper);
      this.migrated.add(htmlElement);
      
      this.log(`Migrated element ${index + 1}: ${config.animation}`);
    });
  }

  /**
   * Migrate .stats-counter elements to <pm-counter-animation>
   */
  private migrateStatsCounters(): void {
    const counters = document.querySelectorAll('.stats-counter:not(pm-counter-animation):not(pm-counter-animation *)');
    
    this.log(`Found ${counters.length} legacy stats counters`);
    
    counters.forEach((counter, index) => {
      const htmlElement = counter as HTMLElement;
      
      if (this.migrated.has(htmlElement)) return;
      
      const target = htmlElement.getAttribute('data-target') || htmlElement.textContent || '0';
      const component = document.createElement('pm-counter-animation');
      
      component.setAttribute('target', target);
      
      // Copy classes for styling
      const classes = Array.from(htmlElement.classList).filter(cls => !cls.startsWith('stats-'));
      component.className = classes.join(' ');
      
      // Replace element
      htmlElement.parentNode?.replaceChild(component, htmlElement);
      this.migrated.add(htmlElement);
      
      this.log(`Migrated counter ${index + 1}: target=${target}`);
    });
  }

  /**
   * Analyze legacy element to determine animation type
   */
  private analyzeLegacyElement(element: HTMLElement): LegacyToLitMapping {
    const classList = Array.from(element.classList);
    
    let animation = 'fade'; // default
    let delay = 0;
    
    // Determine animation type from classes
    if (classList.includes('fade-only')) {
      animation = 'fade-only';
    } else if (classList.includes('scale')) {
      animation = 'scale';
    } else if (classList.includes('slide-left')) {
      animation = 'slide-left';
    } else if (classList.includes('slide-right')) {
      animation = 'slide-right';
    } else if (classList.includes('slide-up')) {
      animation = 'slide-up';
    }
    
    // Extract delay from transition-delay if present
    const computedStyle = window.getComputedStyle(element);
    const transitionDelay = computedStyle.transitionDelay;
    if (transitionDelay && transitionDelay !== '0s') {
      delay = parseFloat(transitionDelay) * 1000; // Convert to ms
    }
    
    return { animation, delay };
  }

  /**
   * Create pm-scroll-reveal component with configuration
   */
  private createScrollRevealComponent(config: LegacyToLitMapping): HTMLElement {
    const component = document.createElement('pm-scroll-reveal');
    
    component.setAttribute('animation', config.animation);
    
    if (config.delay && config.delay > 0) {
      component.setAttribute('delay', config.delay.toString());
    }
    
    if (config.stagger) {
      component.setAttribute('stagger', 'true');
    }
    
    return component;
  }

  /**
   * Check if element is part of a staggered animation
   */
  private isStaggeredElement(element: HTMLElement): boolean {
    return element.classList.contains('stagger-item') || 
           element.closest('.stagger-container') !== null;
  }

  /**
   * Get all sibling elements that should be staggered together
   */
  private getStaggerSiblings(element: HTMLElement): HTMLElement[] {
    const container = element.closest('.stagger-container');
    if (!container) return [element];
    
    return Array.from(container.querySelectorAll('.stagger-item'));
  }

  /**
   * Wrap an element with a Lit component
   */
  private wrapElement(element: HTMLElement, wrapper: HTMLElement): void {
    const parent = element.parentNode;
    if (!parent) return;
    
    parent.insertBefore(wrapper, element);
    wrapper.appendChild(element);
    
    // Remove legacy classes from wrapped element
    element.classList.remove('scroll-reveal', 'fade-only', 'scale', 'slide-left', 'slide-right', 'slide-up');
  }

  /**
   * Clean up remaining legacy classes and styles
   */
  private cleanupLegacyClasses(): void {
    // Remove transition delays that are now handled by components
    const elementsWithDelays = document.querySelectorAll('[style*="transition-delay"]');
    elementsWithDelays.forEach(element => {
      const htmlElement = element as HTMLElement;
      if (this.migrated.has(htmlElement) || htmlElement.closest('pm-scroll-reveal')) {
        htmlElement.style.transitionDelay = '';
      }
    });
    
    this.log('Legacy classes cleaned up');
  }

  /**
   * Check if element should be skipped (Bootstrap structural elements)
   */
  private shouldSkipElement(element: HTMLElement): boolean {
    const classList = Array.from(element.classList);
    
    // Skip Bootstrap columns
    const isBootstrapColumn = classList.some(cls => 
      cls.startsWith('col-') || 
      cls === 'col'
    );
    
    // Skip Bootstrap containers and layout
    const isBootstrapContainer = classList.some(cls => 
      cls.startsWith('container') || 
      cls === 'row' || 
      cls.startsWith('d-') || 
      cls.startsWith('flex-')
    );
    
    // Skip Bootstrap components
    const isBootstrapComponent = classList.some(cls => 
      cls === 'card' || 
      cls === 'nav' || 
      cls === 'navbar' || 
      cls === 'dropdown' || 
      cls === 'modal'
    );
    
    // Skip stagger containers (but allow stagger items)
    const isStaggerContainer = classList.includes('stagger-container');
    
    return isBootstrapColumn || isBootstrapContainer || isBootstrapComponent || isStaggerContainer;
  }

  /**
   * Debug logging
   */
  private log(message: string): void {
    if (this.debug) {
      console.log(`[LegacyMigration] ${message}`);
    }
  }

  /**
   * Public method to check migration status
   */
  public getMigrationStats(): { migrated: number; remaining: number } {
    const remaining = document.querySelectorAll('.scroll-reveal:not(pm-scroll-reveal):not(pm-scroll-reveal *)').length;
    return {
      migrated: this.migrated.size,
      remaining
    };
  }
}

// Auto-migration function
function autoMigrateLegacyElements(enableDebug: boolean = false): void {
  const migration = new LegacyMigration(enableDebug);
  migration.migrateAll();
  
  // Expose migration stats globally
  (window as any).legacyMigrationStats = migration.getMigrationStats();
}

// Export for use in lit-entry
export { LegacyMigration, autoMigrateLegacyElements };